/*
 * Chef In De Box
 * Custom storefront functionality
 *
 * Weekly Box Rules
 * --------------------------------------------------
 * - Minimum 6 qualifying subscription meals per order
 * - Optional add-ons do NOT count toward the 6-meal minimum
 * - Displays cart progress
 * - Prevents storefront checkout below 6 qualifying meals
 * - Supports Horizon cart drawer updates
 *
 * QUALIFYING MEAL LOGIC
 * --------------------------------------------------
 * A cart line counts as a meal when it has a Shopify selling plan.
 * Chef In De Box meals are subscription products.
 * Optional extras/add-ons are sold without a selling plan.
 *
 * NOTE:
 * This is storefront enforcement.
 * A server-side Shopify validation should be added later
 * if the minimum must be impossible to bypass.
 */

(() => {
  'use strict';

  const MINIMUM_MEALS = 6;

  const CONFIG = {
    progressId: 'cidb-box-progress',
    styleId: 'cidb-box-progress-styles',
    updateDelay: 150,
    requestDelay: 250
  };

  let updateTimer = null;
  let requestTimer = null;
  let lastMealCount = null;
  let lastCartItemCount = null;

  /* --------------------------------------------------
     Styles
  -------------------------------------------------- */

  function addStyles() {
    if (document.getElementById(CONFIG.styleId)) return;

    const style = document.createElement('style');

    style.id = CONFIG.styleId;

    style.textContent = `
      #${CONFIG.progressId} {
        margin: 16px 0;
        padding: 16px 18px;
        background: #FFF4E6;
        border-radius: 8px;
        color: #333333;
        line-height: 1.45;
        box-sizing: border-box;
        width: 100%;
      }

      #${CONFIG.progressId} .cidb-box-title {
        display: block;
        margin: 0 0 4px;
        font-weight: 600;
      }

      #${CONFIG.progressId} .cidb-box-count {
        margin: 0;
      }

      #${CONFIG.progressId} .cidb-box-message {
        margin: 4px 0 0;
        opacity: 0.8;
      }

      #${CONFIG.progressId}.cidb-box-ready {
        background: #4C6444;
        color: #FFFFFF;
      }

      #${CONFIG.progressId}.cidb-box-ready .cidb-box-message {
        opacity: 0.9;
      }

      .cidb-checkout-disabled {
        opacity: 0.45 !important;
        cursor: not-allowed !important;
        pointer-events: none !important;
      }
    `;

    document.head.appendChild(style);
  }

  /* --------------------------------------------------
     Shopify cart
  -------------------------------------------------- */

  async function getCart() {
    try {
      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}cart.js`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          cache: 'no-store'
        }
      );

      if (!response.ok) {
        throw new Error(`Cart request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.warn(
        'Chef In De Box: unable to read cart.',
        error
      );

      return null;
    }
  }

  /* --------------------------------------------------
     Qualifying meal count
  -------------------------------------------------- */

  function isQualifyingMeal(item) {
    /*
     * Chef In De Box weekly meals are subscription products.
     * Shopify exposes the active selling plan on cart lines
     * through selling_plan_allocation.
     *
     * Add-ons such as protein balls are normal products and
     * therefore do not have a selling plan allocation.
     */
    return Boolean(item?.selling_plan_allocation);
  }

  function getMealCount(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((total, item) => {
      if (!isQualifyingMeal(item)) return total;

      return total + Number(item.quantity || 0);
    }, 0);
  }

  /* --------------------------------------------------
     Find checkout controls
  -------------------------------------------------- */

  function getCheckoutControls() {
    const selectors = [
      'button[name="checkout"]',
      'input[name="checkout"]',
      'form[action*="/cart"] [type="submit"][name="checkout"]',
      'a[href="/checkout"]',
      'a[href^="/checkout"]',
      '[href="/checkout"]'
    ];

    return Array.from(
      document.querySelectorAll(selectors.join(','))
    ).filter((element, index, array) => {
      return array.indexOf(element) === index;
    });
  }

  /* --------------------------------------------------
     Find best place for progress message
  -------------------------------------------------- */

  function findProgressContainer() {
    const checkout = getCheckoutControls()[0];

    if (checkout) {
      const candidates = [
        checkout.closest('.cart__footer'),
        checkout.closest('[class*="cart-footer"]'),
        checkout.closest('[class*="cart__footer"]'),
        checkout.closest('[class*="cart-drawer"]'),
        checkout.closest('form')
      ];

      for (const candidate of candidates) {
        if (candidate) {
          return {
            container: candidate,
            checkout
          };
        }
      }

      return {
        container: checkout.parentElement,
        checkout
      };
    }

    const fallbackSelectors = [
      'cart-drawer',
      '[class*="cart-drawer"]',
      '[data-cart-drawer]',
      'form[action*="/cart"]'
    ];

    for (const selector of fallbackSelectors) {
      const container = document.querySelector(selector);

      if (container) {
        return {
          container,
          checkout: null
        };
      }
    }

    return null;
  }

  /* --------------------------------------------------
     Progress UI
  -------------------------------------------------- */

  function createProgressElement() {
    let progress =
      document.getElementById(CONFIG.progressId);

    if (progress) return progress;

    progress = document.createElement('div');

    progress.id = CONFIG.progressId;
    progress.setAttribute('role', 'status');
    progress.setAttribute('aria-live', 'polite');

    return progress;
  }

  function renderProgress(mealCount, cartItemCount) {
    const existingProgress =
      document.getElementById(CONFIG.progressId);

    /*
     * Hide the weekly-box message only when the actual
     * Shopify cart is completely empty.
     *
     * An add-on-only cart should still display 0 of 6
     * and remain blocked from checkout.
     */
    if (cartItemCount === 0) {
      if (existingProgress) {
        existingProgress.remove();
      }

      return;
    }

    const target = findProgressContainer();

    if (!target || !target.container) return;

    const progress = createProgressElement();

    /*
     * Horizon can replace the cart drawer HTML after
     * cart mutations, so reinsert our message when needed.
     */
    if (!progress.isConnected) {
      if (
        target.checkout &&
        target.checkout.parentElement
      ) {
        target.checkout.parentElement.insertBefore(
          progress,
          target.checkout
        );
      } else {
        target.container.prepend(progress);
      }
    }

    const remaining = Math.max(
      MINIMUM_MEALS - mealCount,
      0
    );

    if (remaining > 0) {
      progress.classList.remove('cidb-box-ready');

      progress.innerHTML = `
        <span class="cidb-box-title">
          Build your weekly box
        </span>

        <p class="cidb-box-count">
          <strong>${mealCount} of ${MINIMUM_MEALS} meals selected</strong>
        </p>

        <p class="cidb-box-message">
          Add ${remaining} more ${
            remaining === 1 ? 'meal' : 'meals'
          } to complete your box.
        </p>
      `;
    } else {
      progress.classList.add('cidb-box-ready');

      progress.innerHTML = `
        <span class="cidb-box-title">
          Your weekly box is ready ✓
        </span>

        <p class="cidb-box-count">
          <strong>${mealCount} ${
            mealCount === 1 ? 'meal' : 'meals'
          } selected</strong>
        </p>

        <p class="cidb-box-message">
          You're ready to check out.
        </p>
      `;
    }
  }

  /* --------------------------------------------------
     Checkout state
  -------------------------------------------------- */

  function setCheckoutState(mealCount, cartItemCount) {
    const checkoutControls = getCheckoutControls();

    /*
     * If there is anything in the cart, checkout requires
     * at least six qualifying subscription meals.
     *
     * This also blocks an add-on-only cart:
     * 5 protein balls + 0 meals = checkout disabled.
     */
    const shouldDisable =
      cartItemCount > 0 &&
      mealCount < MINIMUM_MEALS;

    checkoutControls.forEach((control) => {
      if (shouldDisable) {
        control.classList.add(
          'cidb-checkout-disabled'
        );

        control.setAttribute(
          'aria-disabled',
          'true'
        );

        control.dataset.cidbCheckoutDisabled =
          'true';

        if (
          control.tagName === 'BUTTON' ||
          control.tagName === 'INPUT'
        ) {
          control.disabled = true;
        }
      } else {
        control.classList.remove(
          'cidb-checkout-disabled'
        );

        control.removeAttribute('aria-disabled');

        delete control.dataset
          .cidbCheckoutDisabled;

        if (
          control.tagName === 'BUTTON' ||
          control.tagName === 'INPUT'
        ) {
          control.disabled = false;
        }
      }
    });
  }

  /* --------------------------------------------------
     Main update
  -------------------------------------------------- */

  async function updateWeeklyBox() {
    const cart = await getCart();

    if (!cart) return;

    const cartItemCount =
      Number(cart.item_count || 0);

    const mealCount =
      getMealCount(cart);

    lastMealCount = mealCount;
    lastCartItemCount = cartItemCount;

    renderProgress(
      mealCount,
      cartItemCount
    );

    setCheckoutState(
      mealCount,
      cartItemCount
    );
  }

  function scheduleUpdate(
    delay = CONFIG.updateDelay
  ) {
    window.clearTimeout(updateTimer);

    updateTimer = window.setTimeout(() => {
      updateWeeklyBox();
    }, delay);
  }

  /* --------------------------------------------------
     Block checkout as additional front-end safeguard
  -------------------------------------------------- */

  function shouldBlockCheckout() {
    return (
      lastCartItemCount !== null &&
      lastCartItemCount > 0 &&
      lastMealCount !== null &&
      lastMealCount < MINIMUM_MEALS
    );
  }

  function showMinimumMessage() {
    renderProgress(
      lastMealCount || 0,
      lastCartItemCount || 0
    );

    const progress =
      document.getElementById(
        CONFIG.progressId
      );

    if (progress) {
      progress.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const checkout = target.closest(
        'button[name="checkout"], ' +
        'input[name="checkout"], ' +
        'a[href="/checkout"], ' +
        'a[href^="/checkout"]'
      );

      if (!checkout) return;

      if (shouldBlockCheckout()) {
        event.preventDefault();
        event.stopImmediatePropagation();

        showMinimumMessage();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (
        !(form instanceof HTMLFormElement)
      ) {
        return;
      }

      const submitter = event.submitter;

      const isCheckout =
        submitter?.name === 'checkout' ||
        form.querySelector(
          '[name="checkout"]'
        );

      if (!isCheckout) return;

      if (shouldBlockCheckout()) {
        event.preventDefault();
        event.stopImmediatePropagation();

        showMinimumMessage();
      }
    },
    true
  );

  /* --------------------------------------------------
     Watch Shopify cart requests
  -------------------------------------------------- */

  function watchFetchRequests() {
    if (!window.fetch) return;

    const originalFetch = window.fetch;

    window.fetch = async function (...args) {
      const response =
        await originalFetch.apply(this, args);

      try {
        const request = args[0];

        const url =
          typeof request === 'string'
            ? request
            : request?.url || '';

        if (
          /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(
            url
          )
        ) {
          window.clearTimeout(
            requestTimer
          );

          requestTimer =
            window.setTimeout(() => {
              scheduleUpdate(100);
            }, CONFIG.requestDelay);
        }
      } catch (error) {
        /*
         * Never interfere with Shopify's
         * original cart request.
         */
      }

      return response;
    };
  }

  /* --------------------------------------------------
     Watch Horizon DOM updates
  -------------------------------------------------- */

  function watchCartDrawer() {
    const observer =
      new MutationObserver((mutations) => {
        let relevantChange = false;

        for (const mutation of mutations) {
          if (
            mutation.type === 'childList' &&
            (
              mutation.addedNodes.length ||
              mutation.removedNodes.length
            )
          ) {
            relevantChange = true;
            break;
          }
        }

        if (relevantChange) {
          scheduleUpdate(200);
        }
      });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /* --------------------------------------------------
     Quantity changes
  -------------------------------------------------- */

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;

      if (
        !(target instanceof HTMLElement)
      ) {
        return;
      }

      if (
        target.matches(
          'input[name="updates[]"], ' +
          '[data-quantity-input], ' +
          'input[type="number"]'
        )
      ) {
        scheduleUpdate(500);
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const quantityControl =
        target.closest(
          '[data-quantity-plus], ' +
          '[data-quantity-minus], ' +
          'quantity-selector button, ' +
          'quantity-input button'
        );

      if (quantityControl) {
        scheduleUpdate(500);
      }
    },
    true
  );

  /* --------------------------------------------------
     Initialise
  -------------------------------------------------- */

  function init() {
    addStyles();
    watchFetchRequests();
    watchCartDrawer();
    updateWeeklyBox();

    console.log(
      `Chef In De Box weekly box rule active: minimum ${MINIMUM_MEALS} subscription meals. Add-ons excluded.`
    );
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }
})();